/**
 * Detail "Move to line…" — the outline bulk handler, run against a selection of one.
 *
 * Same handler the checkbox selection runs, so the record ⌘K and the bulk ⌘K cannot drift apart.
 */
import { computed, inject } from '@angular/core';
import { Entity } from '@jms/model';
import { EntityDataService, EntityDataServiceRaw } from '@workbuddy/services/entities';
import { ToastService } from '@workbuddy/ui/services';
import { WorkBuddyDialogService } from '@workbuddy/ui/v2/blocks/dialog';
import { BOARD_PANEL_HOST } from '@workbuddy/ui/v2/blocks/entity.board/components/board.panel/board.panel.host';
import { EntityBoardStore } from '@workbuddy/ui/v2/blocks/entity.board/state';
import type { ActionCommandCapability, ActionCommandContext, ActionCommandDescriptor, SurfaceContextLike } from '@workbuddy/ui/v2/surface.engine';
import { handleOutlineMoveToLine, OUTLINE_MOVE_TO_LINE_ACTION } from '../bulk/outline.bulk.move';
import { OutlineRowPatcher } from '../bulk/outline.bulk.schedule';
import { NOOP_OUTLINE_ROW_PATCHER, reloadSurfaceRecord } from '../bulk/outline.record.command';
import { outlineRowForTask, OutlineRecordTask, singleOutlineCommandArgs } from '../bulk/outline.record';
import { OUTLINE_MOVE_TO_LINE_LABEL } from '../relation/outline.relation.builder';
import { OutlineMoveToLineCommandConfig } from './outline.detail.command.config';

export function createOutlineMoveToLineCommand<TTask extends OutlineRecordTask & Entity>(config: OutlineMoveToLineCommandConfig<TTask>): ActionCommandCapability<TTask> {

  const dialogs = inject(WorkBuddyDialogService);
  const entityData = inject(EntityDataService);
  const entityDataRaw = inject(EntityDataServiceRaw);
  const toasts = inject(ToastService);
  const board = inject(EntityBoardStore, { optional: true });
  const panel = inject(BOARD_PANEL_HOST, { optional: true });

  return {
    descriptor: ({ page }: ActionCommandContext<TTask>) => computed<ActionCommandDescriptor>(() => ({
      id: OUTLINE_MOVE_TO_LINE_ACTION,
      label: OUTLINE_MOVE_TO_LINE_LABEL,
      iconLeft: 'fa-regular fa-arrow-right-to-bracket',
      // A subtask moves by reparenting onto another task, not by picking a line.
      hidden: !page.$model()?._id || page.$readonly() || isSubtaskModel(config, page.$model()),
      disabled: page.$saving(),
      pin: 'never',
    })),
    run: async (page: SurfaceContextLike<TTask>) => {

      const record = page.$model();
      const row = record && !page.$readonly() && !config.isSubtask(record) ? outlineRowForTask(record, config.kinds, config) : null;
      if (!row || !record) return;

      // The picker excludes the task's current line, and the parent it lists lines from is the one the
      // task is denormalised onto — a task never moves across plans or templates.
      const parentId = config.parentRef(record)?._id;
      if (!parentId) return;
      const store: OutlineRowPatcher = board ?? NOOP_OUTLINE_ROW_PATCHER;
      const outcome = await handleOutlineMoveToLine(singleOutlineCommandArgs(OUTLINE_MOVE_TO_LINE_ACTION, row), { kinds: config.kinds, reparentCommand: config.reparentCommand, dialogs, entityData, store, toasts, parentId });
      // `clearSelection: false` is how the handler says the picker was dismissed or the move failed.
      if (outcome.clearSelection === false) return;
      // The move rewrote the owning line and the parent, which the Detail renders and its next patch
      // would otherwise send back stale.
      await reloadSurfaceRecord(entityDataRaw, config.entity, page, record._id, config.toModel);
      if (!board) panel?.refresh('collection');
    },
  };
}

/** Null-safe subtask probe for the descriptor, which runs before the model has loaded. */
function isSubtaskModel<TTask extends OutlineRecordTask>(config: OutlineMoveToLineCommandConfig<TTask>, model: TTask | null | undefined): boolean {

  return !!model && config.isSubtask(model);
}
